import * as React from 'react';
import { styled } from '@linaria/react';

import { normalize } from '@/utils';

import { getCurrentCommandStartTime } from './SelfDrawingPath.helpers';
import type { Command } from './SelfDrawingPath.types';

interface Props {
  time: number;
  commands: Array<Command>;
  currentCommandIndex: number;
}

function DrawnPathParts({
  time,
  commands,
  currentCommandIndex,
}: Props) {
  const fullyDrawnCommands = React.useMemo(() => {
    return commands.slice(0, currentCommandIndex);
  }, [commands, currentCommandIndex]);

  const currentlyDrawingCommand =
    commands[currentCommandIndex]?.type === 'move'
      ? null
      : commands[currentCommandIndex];

  let currentLine;

  if (currentlyDrawingCommand) {
    const currentCommandStartTime = getCurrentCommandStartTime(
      time,
      commands
    );

    // Figure out the x1/y1/x2/y2 for the line:
    const previousCommand =
      fullyDrawnCommands[currentCommandIndex - 1];

    if (!previousCommand) {
      throw new Error(
        'Previous command is required for line drawing'
      );
    }

    const { x: x1, y: y1 } = previousCommand.to;

    const progressThroughCurrentCommand =
      (time - currentCommandStartTime) /
      currentlyDrawingCommand.duration;

    const x2 = normalize(
      progressThroughCurrentCommand,
      0,
      1,
      x1,
      currentlyDrawingCommand.to.x
    );
    const y2 = normalize(
      progressThroughCurrentCommand,
      0,
      1,
      y1,
      currentlyDrawingCommand.to.y
    );

    currentLine = <Line x1={x1} y1={y1} x2={x2} y2={y2} />;
  }

  return (
    <>
      {fullyDrawnCommands.map((command, index) => {
        /* NOTE: only supports lines atm. */
        if (command.type !== 'line') {
          return null;
        }

        const previousCommand = fullyDrawnCommands[index - 1];

        if (!previousCommand) {
          throw new Error(
            'Previous command is required for line drawing'
          );
        }

        return (
          <Line
            key={index}
            x1={previousCommand.to.x}
            y1={previousCommand.to.y}
            x2={command.to.x}
            y2={command.to.y}
          />
        );
      })}
      {currentLine}
    </>
  );
}

const Line = styled.line`
  stroke: white;
  stroke-width: 1px;
  stroke-linecap: round;
`;

export default DrawnPathParts;
